"use client"

import { useEffect, useMemo, useCallback } from "react"
import { useRouter, usePathname, useSearchParams } from "next/navigation"
import { useQuery } from "@tanstack/react-query"
import errorToast from "../../../shared/lib/toast/error.toast"
import VolunteersTable from "./volunteers-table"
import { useVolunteerTableRows, useVolunteerOrganizationNames, useVolunteersListQueryParams } from "../hooks/volunteers.hooks"
import { getVolunteerTableRows } from "../actions/volunteers.actions"
import { usePaginationData } from "../../../shared/context/pagination-data-context"
import VolunteersResultsCount from "./volunteers-results-count"
import VolunteerFilterIndicator from "./volunteer-filter-indicator"
import { RegionalVolunteersRoster, calculateRegionalRosterStats, type RegionalRosterStats } from "./regional-volunteers-roster"
import { getLocalActiveTodayQueryParams } from "../lib/local-active-today-bounds"

function statsEqual(a: RegionalRosterStats | undefined, b: RegionalRosterStats | undefined) {
    if (!a || !b) return false
    return (
        a.constructionSkills === b.constructionSkills &&
        a.otherSkills === b.otherSkills &&
        a.potentialTeamLeads === b.potentialTeamLeads &&
        a.cooking === b.cooking &&
        a.dietaryRestrictions === b.dietaryRestrictions &&
        a.males === b.males &&
        a.females === b.females
    )
}

/**
 * Volunteers list component - now shows all non-homeowner users
 */
function VolunteersList({ canManageVolunteers = false }: { canManageVolunteers?: boolean }) {
    const { setPaginationData } = usePaginationData()
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const volunteerQueryParams = useVolunteersListQueryParams()
    const localDateKey = new Date().toLocaleDateString("en-CA")
    const activeTodayQueryParams = useMemo(() => getLocalActiveTodayQueryParams(), [localDateKey])

    const { data, isLoading } = useVolunteerTableRows()
    const { data: orgNamesData } = useVolunteerOrganizationNames()

    const total = useMemo(() => {
        if (!data?.success || !data.data) {
            return 0
        }
        return data.data.total
    }, [data])

    const activeTodayCount = useMemo(() => {
        if (!data?.success || !data.data) return 0
        return data.data.activeTodayCount ?? 0
    }, [data])

    // Read filter params from URL for column filters
    const searchFilter = searchParams.get("search") ?? ""
    const rolesFilter = searchParams.get("roles") ?? ""
    const organizationFilter = searchParams.get("organization") ?? ""
    const arrivalDateFilter = searchParams.get("arrivalDate") ?? ""
    const departureDateFilter = searchParams.get("departureDate") ?? ""
    const addedFromFilter = searchParams.get("addedFrom") ?? ""
    const addedToFilter = searchParams.get("addedTo") ?? ""
    const lastActiveAtFromFilter = searchParams.get("lastActiveAtFrom") ?? ""
    const lastActiveAtToFilter = searchParams.get("lastActiveAtTo") ?? ""
    const statusFilter = searchParams.get("status") ?? ""
    const serveDatesRegionId = searchParams.get("serveDatesRegionId") ?? ""
    const showRegionalRoster = canManageVolunteers && serveDatesRegionId.trim().length > 0
    const currentRows = data?.success ? (data.data?.users ?? []) : []
    const backendRegionalStats = data?.success ? data.data?.regionalRosterStats : undefined
    const visiblePageStats = useMemo(() => calculateRegionalRosterStats(currentRows), [currentRows])
    const shouldFetchRegionalStatsFallback =
        showRegionalRoster &&
        total > currentRows.length &&
        (!backendRegionalStats || statsEqual(backendRegionalStats, visiblePageStats))
    const regionalStatsFallbackQuery = useQuery({
        queryKey: ['regional-roster-stats-fallback', volunteerQueryParams, activeTodayQueryParams.activeTodayStart, activeTodayQueryParams.clientTimeZone, total],
        enabled: shouldFetchRegionalStatsFallback,
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        queryFn: async () => {
            const pageSize = 100
            const first = await getVolunteerTableRows({
                ...volunteerQueryParams,
                ...activeTodayQueryParams,
                page: 1,
                limit: pageSize,
            })
            if (!first.success) {
                throw new Error(first.message ?? 'Failed to fetch regional roster stats')
            }

            const users = [...first.data.users]
            const pageCount = Math.ceil((first.data.total ?? 0) / pageSize)
            if (pageCount > 1) {
                const rest = await Promise.all(
                    Array.from({ length: pageCount - 1 }, (_, index) =>
                        getVolunteerTableRows({
                            ...volunteerQueryParams,
                            ...activeTodayQueryParams,
                            page: index + 2,
                            limit: pageSize,
                        })
                    )
                )
                for (const response of rest) {
                    if (response.success && response.data?.users) {
                        users.push(...response.data.users)
                    }
                }
            }

            return calculateRegionalRosterStats(users)
        },
    })
    const regionalRosterStats = regionalStatsFallbackQuery.data ?? backendRegionalStats

    const setParams = useCallback(
        (updates: Record<string, string | null>) => {
            const params = new URLSearchParams(searchParams.toString())
            for (const [key, value] of Object.entries(updates)) {
                if (value != null && value !== "") {
                    params.set(key, value)
                } else {
                    params.delete(key)
                }
            }
            params.set("page", "1")
            router.push(`${pathname}?${params.toString()}`)
        },
        [pathname, router, searchParams]
    )

    const onRolesFilter = useCallback(
        (roles: string[] | null) => {
            setParams({ roles: roles?.length ? roles.join(",") : null })
        },
        [setParams]
    )

    const onSearchFilter = useCallback(
        (value: string | null) => {
            setParams({ search: value?.trim() || null })
        },
        [setParams]
    )

    const onOrganizationFilter = useCallback(
        (value: string | null) => {
            setParams({ organization: value?.trim() || null })
        },
        [setParams]
    )

    const onArrivalDateFilter = useCallback(
        (value: string | null) => {
            setParams({ arrivalDate: value?.trim() || null })
        },
        [setParams]
    )

    const onDepartureDateFilter = useCallback(
        (value: string | null) => {
            setParams({ departureDate: value?.trim() || null })
        },
        [setParams]
    )

    const onAddedFilter = useCallback(
        (from: string | null, to: string | null) => {
            setParams({
                addedFrom: from?.trim() || null,
                addedTo: to?.trim() || null,
            })
        },
        [setParams]
    )

    const onLastActiveFilter = useCallback(
        (from: string | null, to: string | null) => {
            setParams({
                lastActiveAtFrom: from?.trim() || null,
                lastActiveAtTo: to?.trim() || null,
            })
        },
        [setParams]
    )

    const onStatusFilter = useCallback(
        (value: string | null) => {
            setParams({ status: value?.trim() || null })
        },
        [setParams]
    )

    useEffect(() => {
        if (data?.success && data.data) {
            setPaginationData({
                page: data.data.page,
                limit: data.data.limit,
                total: data.data.total
            })
        }
    }, [data, setPaginationData])

    if (data && !data.success) {
        errorToast(data?.message || "Something went wrong while fetching users")
        return null
    }

    return (
        <div className="space-y-4">
            <VolunteerFilterIndicator
                summary={<VolunteersResultsCount total={total} activeTodayCount={activeTodayCount} isLoading={isLoading} />}
            />
            {showRegionalRoster ? (
                <RegionalVolunteersRoster
                    data={currentRows}
                    isLoading={isLoading}
                    stats={regionalRosterStats}
                />
            ) : (
                <VolunteersTable
                    data={data?.data?.users ?? []}
                    isLoading={isLoading}
                    organizationSuggestions={orgNamesData?.success ? (orgNamesData.data ?? []) : undefined}
                    searchFilter={searchFilter}
                    onSearchFilter={onSearchFilter}
                    rolesFilter={rolesFilter}
                    onRolesFilter={onRolesFilter}
                    organizationFilter={organizationFilter}
                    onOrganizationFilter={onOrganizationFilter}
                    arrivalDateFilter={arrivalDateFilter}
                    onArrivalDateFilter={onArrivalDateFilter}
                    departureDateFilter={departureDateFilter}
                    onDepartureDateFilter={onDepartureDateFilter}
                    addedFromFilter={addedFromFilter}
                    addedToFilter={addedToFilter}
                    onAddedFilter={onAddedFilter}
                    lastActiveAtFromFilter={lastActiveAtFromFilter}
                    lastActiveAtToFilter={lastActiveAtToFilter}
                    onLastActiveFilter={onLastActiveFilter}
                    statusFilter={statusFilter}
                    onStatusFilter={onStatusFilter}
                />
            )}
        </div>
    )
}

export default VolunteersList